import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  AvailabilityDot,
  getAvailabilityDotStatus,
} from "shared-ui/src/components/ecommerce/AvailabilityDot";
import { ProductPartNumbers } from "shared-ui/src/components/ecommerce/ProductPartNumbers";
import {
  AvailabilityVerificationProvider,
  AvailabilityVerifier,
} from "@features/ecommerce/availability/AvailabilityVerifier";
import { validateEmail } from "@utils/validators";
import { fmtCurrency, QuantitySpinner } from "../quoteUi";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import AddressSelection from "../AddressSelection";
import type { QuoteAddressOption } from "../hooks/useQuoteAddresses";
import { DESCRIPTION_MAX_LENGTH, type ComposedLine } from "./types";

// ─── Vertical stepper ──────────────────────────────────────────────────────────

/**
 * Left-rail step indicator shared by the page (gated Next/Back) and the dialog (scroll-spy).
 * `onSelect(i)` is what differs between hosts: the page switches the active step, the dialog
 * scrolls to the section.
 */
export function VerticalStepper({
  steps,
  activeStep,
  onSelect,
  completed,
}: {
  steps: readonly string[];
  activeStep: number;
  onSelect: (i: number) => void;
  completed?: readonly boolean[];
}) {
  return (
    <Box
      sx={{
        width: 140,
        flexShrink: 0,
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
        py: 3,
        px: 1,
      }}
    >
      {steps.map((label, i) => {
        // A step is "done" when its own state is satisfied (e.g. a shipping method is selected),
        // not merely when the active step has moved past it.
        const isDone = completed?.[i] ?? i < activeStep;
        return (
        <Box
          key={label}
          role="button"
          tabIndex={0}
          aria-current={i === activeStep ? "step" : undefined}
          aria-label={`${label}, ${isDone ? "completed" : i === activeStep ? "current step" : "not started"}`}
          onClick={() => onSelect(i)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelect(i);
            }
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: i < steps.length - 1 ? 1 : undefined,
            cursor: "pointer",
            "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", borderRadius: 1 },
          }}
        >
          <Box
            sx={{
              width: "1.8rem",
              height: "1.8rem",
              borderRadius: "50%",
              bgcolor:
                i === activeStep
                  ? "primary.main"
                  : isDone
                    ? "success.light"
                    : "transparent",
              border: !isDone && i !== activeStep ? "2px solid" : "none",
              borderColor: "action.disabled",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isDone ? (
              <CheckIcon sx={{ color: "success.contrastText", fontSize: 16 }} />
            ) : (
              <Typography
                sx={{
                  color: i === activeStep ? "primary.contrastText" : "text.disabled",
                  fontSize: 13,
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                {i + 1}
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ textAlign: "center", mt: 1, lineHeight: 1.3, px: 0.5, fontWeight: 500, color: "text.primary" }}
          >
            {label}
          </Typography>
          {i < steps.length - 1 && (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "center", py: 0.5, minHeight: 48 }}>
              <Box sx={{ width: 2, bgcolor: "action.disabled" }} />
            </Box>
          )}
        </Box>
        );
      })}
    </Box>
  );
}

// ─── Product thumbnail ─────────────────────────────────────────────────────────

export function ProductThumb({ src, size }: { src?: string; size: number }) {
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt=""
        loading="lazy"
        sx={{ width: size, height: size, objectFit: "contain", flexShrink: 0, borderRadius: 0.5 }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 0.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "action.hover",
      }}
    >
      <ImageOutlinedIcon sx={{ fontSize: size * 0.5, color: "text.disabled" }} />
    </Box>
  );
}

const LINE_AVAILABILITY_SX = { display: "inline-flex", "& .MuiTypography-root": { fontSize: 12 } };

/**
 * Stock status as a dot and text (not a chip), sized for the caption line under the product name.
 * An unchecked ("Verify Stock") status is checked live on click when the user may read cart
 * availability; otherwise it is a static grey dot, never a disabled button.
 */
function LineAvailability({
  label,
  productId,
  canVerify,
}: {
  label: string;
  productId: number;
  canVerify: boolean;
}) {
  const status = getAvailabilityDotStatus(label);
  if (status === "unverified" && canVerify) {
    return (
      <Box sx={LINE_AVAILABILITY_SX}>
        <AvailabilityVerifier
          itemId={productId}
          sourceStatus={label}
          size={6}
          telemetry={{
            name: "quote_composer_verify_stock",
            section: "quote_composer",
            label: "Verify Stock",
            id: productId.toString(),
          }}
        />
      </Box>
    );
  }
  return (
    <AvailabilityDot
      status={status === "unverified" ? "unknown" : status}
      // Keep the source wording ("In Stock (12 units)"), but one spelling of "Verify Stock".
      label={status === "unverified" ? "Verify Stock" : label}
      size={6}
      sx={LINE_AVAILABILITY_SX}
    />
  );
}

// ─── Product lines table ───────────────────────────────────────────────────────

/**
 * Renders the composed quote lines (photo, name, MFR#, availability, unit price, qty, total).
 * Reused by the page (catalog search adds lines) and the dialog (cart seeds lines).
 * `onRemove` is optional: the cart-fed dialog hides per-line removal to match the prototype.
 */
export function ProductLinesTable({
  lines,
  onQtyChange,
  onRemove,
  onNameClick,
  minQty = 0,
  emptyText = "No products yet.",
  imagesByProductId,
  availabilityByProductId,
  mfrPartByProductId,
  canVerifyAvailability = false,
  currencyId = 1,
}: {
  lines: ComposedLine[];
  onQtyChange: (id: number, qty: number) => void;
  onRemove?: (id: number) => void;
  /** When provided, the product name becomes a button that opens the Product Details Drawer. */
  onNameClick?: (productId: number) => void;
  minQty?: number;
  emptyText?: string;
  /** Thumbnails resolved separately (e.g. the cart endpoint); overrides the line product's imageUrl when present. */
  imagesByProductId?: Record<number, string>;
  /** Availability labels resolved separately (e.g. the cart endpoint); overrides the line product's inventoryStatus. */
  availabilityByProductId?: Record<number, string>;
  /** Mfr part numbers resolved separately (e.g. the cart endpoint); overrides the line product's manufacturerPartNumber. */
  mfrPartByProductId?: Record<number, string>;
  /** Lets an unchecked ("Verify Stock") line be checked live; needs permission to read cart availability. */
  canVerifyAvailability?: boolean;
  currencyId?: number;
}) {
  const currency = React.useMemo(
    () => createSavedCurrencyFormatter(currencyId),
    [currencyId],
  );
  if (lines.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          color: "text.secondary",
          border: "1px dashed",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="body2">{emptyText}</Typography>
      </Box>
    );
  }

  // Shared so the same product on two lines verifies once.
  const table = (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small" sx={{ tableLayout: "fixed", "& tbody tr:last-child td": { border: 0 } }}>
        <TableHead>
          <TableRow sx={{ "& th": { bgcolor: "table.header", fontWeight: 500, fontSize: 14 } }}>
            <TableCell>Product Included</TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              Unit Price
            </TableCell>
            <TableCell align="center" sx={{ width: 130 }}>
              Qty
            </TableCell>
            <TableCell align="right" sx={{ width: 120 }}>
              Total
            </TableCell>
            {onRemove && <TableCell sx={{ width: 56 }} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line) => {
            const product = line.product;
            const qty = line.quantity;
            const excluded = qty === 0;
            const availability =
              availabilityByProductId?.[product.productId] ?? product.inventoryStatus;
            const mfrPart =
              mfrPartByProductId?.[product.productId] ?? product.manufacturerPartNumber;
            const shiPart = product.shiPartNumber ?? (product.productId > 0 ? String(product.productId) : undefined);
            // A fabricated quote line has no catalog product, so there is no product detail to open.
            const nameClick = product.productId > 0 ? onNameClick : undefined;
            return (
              <TableRow key={line.id} hover sx={excluded ? { opacity: 0.55 } : undefined}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <ProductThumb src={imagesByProductId?.[product.productId] ?? product.imageUrl} size={44} />
                    <Box sx={{ minWidth: 0 }}>
                      {nameClick ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          underline="hover"
                          onClick={() => nameClick(product.productId)}
                          sx={{
                            fontWeight: 500,
                            textAlign: "left",
                            display: "block",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textDecoration: excluded ? "line-through" : undefined,
                          }}
                        >
                          {product.productName}
                        </Link>
                      ) : (
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color="primary.main"
                          noWrap
                          sx={{ textDecoration: excluded ? "line-through" : undefined }}
                        >
                          {product.productName}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.25 }}>
                        <ProductPartNumbers
                          mfrPartNumber={mfrPart}
                          shiPartNumber={shiPart}
                          telemetry={{
                            namePrefix: "quote_composer",
                            section: "quote_composer",
                            id: product.productId?.toString(),
                          }}
                        />
                        {excluded ? (
                          <Tooltip title="Quantity is 0, so this line won't be added. Use + to include it again.">
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label="Excluded"
                              tabIndex={0}
                            />
                          </Tooltip>
                        ) : (
                          availability && (
                            <LineAvailability
                              label={availability}
                              productId={product.productId}
                              canVerify={canVerifyAvailability}
                            />
                          )
                        )}
                      </Box>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2" color="text.primary">
                    {product.unitPrice != null ? currency.format(product.unitPrice) : "—"}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <QuantitySpinner value={qty} onChange={(v) => onQtyChange(line.id, v)} min={minQty} />
                  </Box>
                </TableCell>

                <TableCell align="right">
                  <Typography variant="body2" fontWeight={500} color="text.primary">
                    {currency.format((product.unitPrice ?? 0) * qty)}
                  </Typography>
                </TableCell>

                {onRemove && (
                  <TableCell align="center">
                    <Tooltip title="Remove">
                      <IconButton size="small" aria-label="remove line" onClick={() => onRemove(line.id)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
  return <AvailabilityVerificationProvider>{table}</AvailabilityVerificationProvider>;
}

// ─── Shipping & tax step ───────────────────────────────────────────────────────

export function ShippingStep({
  createQuotesEnabled,
  shippingOrgUnitId,
  selectedShippingAddressId,
  onSelectAddress,
  shippingMethods,
  shippingCheckPending,
  shippingCheckError,
  hasCheckedShipping,
  hasLines,
  selectedShippingMethodId,
  onSelectMethod,
  includeTax,
  onToggleTax,
  taxPending,
  taxError,
  taxAmount,
  includeShipping,
  onIncludeShippingChange,
  currencyId = 1,
}: {
  createQuotesEnabled: boolean;
  shippingOrgUnitId: number | null;
  selectedShippingAddressId: number | null;
  onSelectAddress: (address: QuoteAddressOption | null) => void;
  shippingMethods: { id?: number; carrier?: string; shippingType?: string; freight?: number }[];
  shippingCheckPending: boolean;
  shippingCheckError: boolean;
  hasCheckedShipping: boolean;
  hasLines: boolean;
  selectedShippingMethodId: number | null;
  onSelectMethod: (id: number | null) => void;
  includeTax: boolean;
  onToggleTax: (next: boolean) => void;
  taxPending: boolean;
  taxError: boolean;
  taxAmount?: number;
  includeShipping: boolean;
  onIncludeShippingChange: (checked: boolean) => void;
  currencyId?: number;
}) {
  if (!createQuotesEnabled) {
    return (
      <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shipping and tax aren't calculated for quotes created in ShiOne yet, so this quote will be
          created without them. (This step is reserved for when estimates are available.)
        </Typography>
        <FormControlLabel control={<Switch checked={false} disabled />} label="Include shipping" />
        <FormControlLabel control={<Switch checked={false} disabled />} label="Include tax" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={includeShipping}
            onChange={(e) => onIncludeShippingChange(e.target.checked)}
          />
        }
        label="Include Shipping"
      />

      {includeShipping && (
        <>
          <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 3 }}>
            <AddressSelection
              orgUnitId={shippingOrgUnitId}
              selectedAddressId={selectedShippingAddressId}
              onSelect={onSelectAddress}
            />

            {!hasLines && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Add at least one product before selecting a shipping address.
              </Alert>
            )}
          </Box>

          {shippingCheckError && (
            <Alert severity="error">
              Shipping methods could not be loaded. Check the address and try again.
            </Alert>
          )}

          {shippingCheckPending && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {hasCheckedShipping && !shippingCheckError && (
            <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 3 }}>
              <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 2 }}>
                Shipping Method
              </Typography>

              {shippingMethods.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No shipping methods were returned for this address.
                </Typography>
              ) : (
                <RadioGroup
                  value={selectedShippingMethodId ?? ""}
                  onChange={(e) => onSelectMethod(e.target.value === "" ? null : Number(e.target.value))}
                >
                  <Box sx={{ display: "grid", gap: 1.5 }}>
                    {shippingMethods.map((method, index) => {
                      const selectable = method.id != null;
                      const isSelected = selectable && method.id === selectedShippingMethodId;
                      return (
                        <Box
                          key={`${method.id ?? "method"}-${index}`}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            border: "1px solid",
                            borderColor: isSelected ? "primary.main" : "divider",
                            borderRadius: 1,
                            bgcolor: "background.paper",
                            p: 2,
                          }}
                        >
                          <FormControlLabel
                            value={method.id ?? ""}
                            control={<Radio disabled={!selectable} />}
                            sx={{ m: 0, flex: 1, minWidth: 0 }}
                            label={
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  {method.shippingType ?? method.carrier ?? "Shipping method"}
                                </Typography>
                                {method.carrier && (
                                  <Typography variant="caption" color="text.secondary">
                                    {method.carrier}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <Typography variant="body2" fontWeight={500}>
                            {fmtCurrency(method.freight, currencyId)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </RadioGroup>
              )}
            </Box>
          )}
        </>
      )}

      {includeShipping && (
        <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeTax}
                onChange={(e) => onToggleTax(e.target.checked)}
                disabled={taxPending}
              />
            }
            label="Include estimated tax on this quote"
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            Estimated tax is based on the ship-to address, shipping method, and products in the
            quote.
          </Typography>

          {includeTax && selectedShippingMethodId == null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Select a shipping method above to calculate estimated tax.
            </Typography>
          )}

          {selectedShippingMethodId != null && (
            <>
              {taxPending && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Estimating tax…
                  </Typography>
                </Box>
              )}

              {taxError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Tax could not be estimated. Turn this off to create the quote without tax, or try again.
                </Alert>
              )}

              {includeTax && !taxPending && !taxError && taxAmount != null && (
                <Typography variant="body2" fontWeight={600} sx={{ mt: 2 }}>
                  Estimated tax: {fmtCurrency(taxAmount, currencyId)}
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Quote details step ────────────────────────────────────────────────────────

export function QuoteDetailsStep({
  name,
  setName,
  description,
  setDescription,
  userEmail,
  sendCopyToSelf,
  setSendCopyToSelf,
  sendToOthers,
  setSendToOthers,
  emailRecipients,
  setEmailRecipients,
  emailMessage,
  setEmailMessage,
  disabled,
  nameError = false,
}: {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  userEmail?: string;
  sendCopyToSelf: boolean;
  setSendCopyToSelf: (v: boolean) => void;
  sendToOthers: boolean;
  setSendToOthers: (v: boolean) => void;
  emailRecipients: string[];
  setEmailRecipients: React.Dispatch<React.SetStateAction<string[]>>;
  emailMessage: string;
  setEmailMessage: (v: string) => void;
  disabled: boolean;
  nameError?: boolean;
}) {
  const [recipientInput, setRecipientInput] = React.useState("");
  const [recipientError, setRecipientError] = React.useState("");

  // Split on comma/semicolon (the reference EmailOrderInvoicesDialog uses the same delimiters),
  // validate each, dedupe case-insensitively against what is already chipped.
  const addRecipients = (raw: string) => {
    const candidates = raw
      .split(/[;,]/)
      .map((v) => v.trim())
      .filter(Boolean);
    if (candidates.length === 0) return;

    const valid: string[] = [];
    const invalid: string[] = [];
    candidates.forEach((c) => (validateEmail(c) ? valid.push(c) : invalid.push(c)));

    if (valid.length > 0) {
      const existing = new Set(emailRecipients.map((r) => r.toLowerCase()));
      const merged = [...emailRecipients];
      valid.forEach((v) => {
        if (!existing.has(v.toLowerCase())) {
          existing.add(v.toLowerCase());
          merged.push(v);
        }
      });
      setEmailRecipients(merged);
    }
    setRecipientError(invalid.length > 0 ? `Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}` : "");
  };

  const commitRecipient = () => {
    if (recipientInput.trim()) addRecipients(recipientInput);
    setRecipientInput("");
  };

  return (
    <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 3 }}>
      <TextField
        fullWidth
        required
        label="Quote name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={disabled}
        error={nameError}
        helperText={nameError ? "Enter a quote name to continue." : undefined}
        sx={{ mb: 2, "& .MuiFormLabel-asterisk": { color: "error.main" } }}
      />
      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        disabled={disabled}
        slotProps={{ htmlInput: { maxLength: DESCRIPTION_MAX_LENGTH } }}
        helperText={`${description.length}/${DESCRIPTION_MAX_LENGTH}`}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={sendCopyToSelf}
              onChange={(e) => setSendCopyToSelf(e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="body2">
              Send a copy to my email address{userEmail ? ` (${userEmail})` : ""}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sendToOthers}
              onChange={(e) => setSendToOthers(e.target.checked)}
              disabled={disabled}
            />
          }
          label={<Typography variant="body2">Send a copy to others</Typography>}
        />
      </Box>

      {sendToOthers && (
        <Box sx={{ ml: 4, mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Autocomplete
            multiple
            freeSolo
            disabled={disabled}
            options={[]}
            value={emailRecipients}
            inputValue={recipientInput}
            onInputChange={(_event, value) => setRecipientInput(value)}
            onChange={(_event, value) => {
              const next: string[] = [];
              const seen = new Set<string>();
              value
                .flatMap((entry) => entry.split(/[;,]/).map((v) => v.trim()))
                .filter((entry) => validateEmail(entry))
                .forEach((entry) => {
                  const key = entry.toLowerCase();
                  if (!seen.has(key)) {
                    seen.add(key);
                    next.push(entry);
                  }
                });
              setEmailRecipients(next);
              setRecipientError("");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email address"
                placeholder={emailRecipients.length === 0 ? "Add email" : ""}
                error={Boolean(recipientError)}
                helperText={recipientError || "Separate email addresses with Enter, comma, or semicolon."}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "," || e.key === ";") {
                    e.preventDefault();
                    commitRecipient();
                  }
                }}
                onBlur={commitRecipient}
              />
            )}
          />

          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Add a message..."
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            disabled={disabled}
            inputProps={{ "aria-label": "Message" }}
          />
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        This quote will be valid through the end of the current month. Once it expires, the prices
        will then follow market changes.
      </Alert>
    </Box>
  );
}

// ─── Summary panel ─────────────────────────────────────────────────────────────

function SummaryRow({
  label,
  value,
  bold,
  primaryLabel,
  pending,
}: {
  label: string;
  value: string;
  bold?: boolean;
  primaryLabel?: boolean;
  pending?: boolean;
}) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Typography
        variant="body2"
        color={primaryLabel ? "text.primary" : "text.secondary"}
        fontWeight={bold ? 600 : 400}
      >
        {label}
      </Typography>
      {pending ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">
            Calculating…
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" fontWeight={bold ? 600 : 400}>
          {value}
        </Typography>
      )}
    </Box>
  );
}

/** Shown when the composer previews the session storefront's pricing for a different target storefront. */
export function OtherStorefrontPricingNotice({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Alert severity="info" sx={sx}>
      Prices shown are from your current storefront and its currency. The quote is priced from the
      selected storefront's catalog, in its currency, when it is created.
    </Alert>
  );
}

export function SummaryPanel({
  productCount,
  totalQuantity,
  subtotal,
  shippingCost,
  taxAmount,
  shippingPending,
  taxPending,
  currencyId = 1,
}: {
  productCount: number;
  totalQuantity: number;
  subtotal: number;
  shippingCost?: number;
  taxAmount?: number;
  shippingPending?: boolean;
  taxPending?: boolean;
  currencyId?: number;
}) {
  const total = subtotal + (shippingCost ?? 0) + (taxAmount ?? 0);
  return (
    <Box sx={{ width: 280, flexShrink: 0, bgcolor: "background.paper", p: 3 }}>
      <Typography variant="overline" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1 }}>
        SUMMARY
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
        <SummaryRow label={`Products · ${productCount}`} value={`${totalQuantity} items`} />
        <SummaryRow label="Subtotal" value={fmtCurrency(subtotal, currencyId)} />
        <SummaryRow
          label="Shipping"
          value={shippingCost != null ? fmtCurrency(shippingCost, currencyId) : "—"}
          pending={shippingPending}
        />
        <SummaryRow
          label="Est. Tax"
          value={taxAmount != null ? fmtCurrency(taxAmount, currencyId) : "—"}
          pending={taxPending}
        />
        <Divider sx={{ my: 0.5 }} />
        <SummaryRow label="Total" value={fmtCurrency(total, currencyId)} bold primaryLabel />
      </Box>
    </Box>
  );
}
